import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getOrderDetail } from "../../api/ordersApi";
import "./OrderDetailModal.css";
import AddToCalendarButtons from "./AddToCalendarButtons";

function formatCurrency(v) {
    const n = Number(v);
    const isCents = Number.isInteger(n) && n >= 50;
    const eur = isCents ? n / 100 : n;
    return eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("it-IT");
}

function formatDateLabel(dateIso) {
    if (!dateIso) return "—";
    return formatDate(dateIso);
}

function formatDateWithSlot(dateIso, slot) {
    const d = formatDateLabel(dateIso);
    if (!slot || slot === "—") return d;
    return `${d} · ${slot}`;
}

const isCrossSell = (item) => item?.id_servizio === 0;

export default function OrderDetailModal({ open, orderId, onClose }) {
    const [detailOrder, setDetailOrder] = useState(null);
    const [detailItems, setDetailItems] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState("");

    useEffect(() => {
        if (!open || !orderId) return;

        let alive = true;
        setDetailLoading(true);
        setDetailError("");
        setDetailOrder(null);
        setDetailItems([]);

        getOrderDetail(orderId)
            .then((data) => {
                if (!alive) return;
                setDetailOrder(data || null);
                setDetailItems(Array.isArray(data?.items) ? data.items : []);
            })
            .catch((e) => {
                if (!alive) return;
                const msg = e?.message || "Errore nel caricamento ordine";
                setDetailError(msg);
                toast.error("Impossibile caricare l'ordine");
            })
            .finally(() => {
                if (alive) setDetailLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [open, orderId]);

    useEffect(() => {
        if (!open) return;

        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };

        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open || !orderId) return null;

    return (
        <div className="modal-overlay" role="presentation" onClick={onClose}>
            <div
                className="modal modal--wide"
                role="dialog"
                aria-modal="true"
                aria-label={`Dettaglio ordine ${orderId}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header-row">
                    <h2 className="modal-title">ordine_{orderId}</h2>
                    <button
                        type="button"
                        className="modal-close"
                        onClick={onClose}
                        aria-label="Chiudi"
                    >
                        ×
                    </button>
                </div>

                <div className="modal-body-scroll">
                    <section className="order-meta">
                        <div className="order-meta-header">
                            <div className="order-meta-id">
                                <span className="order-meta-id-label">Ordine</span>
                                <span className="order-meta-id-value">#{orderId}</span>
                            </div>

                            <div
                                className={
                                    "order-meta-status " +
                                    `order-meta-status--${detailOrder?.raw_status || "unknown"}`
                                }
                            >
                                {detailOrder?.status ?? (detailLoading ? "…" : "—")}
                            </div>
                        </div>

                        <div className="order-meta-grid">
                            <div className="order-meta-card">
                                <span className="order-meta-label">Cliente</span>
                                <span className="order-meta-value">
                                    {detailOrder?.cliente ?? (detailLoading ? "…" : "—")}
                                </span>
                            </div>

                            <div className="order-meta-card">
                                <span className="order-meta-label">Fornitore</span>
                                <span className="order-meta-value">
                                    {detailOrder?.fornitore ?? (detailLoading ? "…" : "—")}
                                </span>
                            </div>

                            <div className="order-meta-card">
                                <span className="order-meta-label">Totale</span>
                                <span className="order-meta-value">
                                    {detailOrder
                                        ? formatCurrency(detailOrder.total_cents)
                                        : detailLoading
                                            ? "…"
                                            : "—"}
                                </span>
                            </div>

                            <div className="order-meta-card">
                                <span className="order-meta-label">Data ordine</span>
                                <span className="order-meta-value">
                                    {detailOrder?.created_at
                                        ? formatDate(detailOrder.created_at)
                                        : detailLoading
                                            ? "…"
                                            : "—"}
                                </span>
                            </div>

                            <div className="order-meta-card">
                                <span className="order-meta-label">Ritiro</span>
                                <span className="order-meta-value">
                                    {detailOrder
                                        ? formatDateWithSlot(
                                            detailOrder.data_ritiro,
                                            detailOrder.ritiro_fascia
                                        )
                                        : detailLoading
                                            ? "…"
                                            : "—"}
                                </span>
                            </div>

                            <div className="order-meta-card">
                                <span className="order-meta-label">Consegna</span>
                                <span className="order-meta-value">
                                    {detailOrder
                                        ? formatDateWithSlot(
                                            detailOrder.data_consegna,
                                            detailOrder.consegna_fascia
                                        )
                                        : detailLoading
                                            ? "…"
                                            : "—"}
                                </span>
                            </div>

                            <div className="order-meta-card order-meta-card--wide">
                                <span className="order-meta-label">Indirizzo ritiro/consegna</span>
                                <span
                                    className="order-meta-value order-meta-value--wrap"
                                    title={detailOrder?.pickup_address?.full_address || ""}
                                >
                                    {detailOrder?.pickup_address?.full_address ||
                                        (detailLoading ? "…" : "—")}
                                </span>
                                {!!detailOrder?.pickup_address?.phone && (
                                    <span className="order-meta-subline">
                                        Telefono: {detailOrder.pickup_address.phone}
                                    </span>
                                )}
                            </div>
                        </div>
                    </section>

                    <AddToCalendarButtons
                        order={detailOrder}
                        actor="customer"
                        disabled={detailLoading}
                        containerClassName="order-meta-actions"
                    />

                    <div
                        className="list-table list-table--details order-detail-table"
                        role="table"
                        aria-label="Dettaglio ordine"
                    >
                        <div className="list-row list-row--head" role="row">
                            <div className="list-cell" role="columnheader">
                                Articolo
                            </div>
                            <div
                                className="list-cell list-ta-center"
                                role="columnheader"
                            >
                                Qta
                            </div>
                            <div
                                className="list-cell list-ta-right"
                                role="columnheader"
                            >
                                Prezzo
                            </div>
                            <div
                                className="list-cell list-ta-right"
                                role="columnheader"
                            >
                                Totale
                            </div>
                        </div>

                        {detailLoading &&
                            Array.from({ length: 4 }).map((_, i) => (
                                <div
                                    className="list-row list-skel"
                                    key={`sk-detail-${i}`}
                                    role="row"
                                    aria-busy="true"
                                >
                                    <div className="list-cell">.</div>
                                    <div className="list-cell">.</div>
                                    <div className="list-cell list-ta-right">.</div>
                                    <div className="list-cell list-ta-right">.</div>
                                </div>
                            ))}

                        {!detailLoading && detailItems.length === 0 && (
                            <div className="list-empty">Nessun articolo.</div>
                        )}

                        {!detailLoading &&
                            detailItems.length > 0 &&
                            detailItems.map((r) => (
                                <div className="list-row" role="row" key={r.id}>
                                    <div className="list-cell" role="cell" data-label="Articolo">
                                        <span className="list-item-title">
                                            {r.descrizione}
                                            {r.tipo ? (
                                                <small className="list-item-subtype">
                                                    {" · "}
                                                    {r.tipo}
                                                </small>
                                            ) : null}
                                        </span>

                                        {isCrossSell(r) && (
                                            <span
                                                className="list-pill list-pill--cross"
                                                title="Articolo suggerito (cross-sell)"
                                            >
                                                Cross-sell
                                            </span>
                                        )}
                                    </div>

                                    <div
                                        className="list-cell list-ta-center"
                                        role="cell"
                                        data-label="Qta"
                                    >
                                        {r.quantita}
                                    </div>

                                    <div
                                        className="list-cell list-ta-right"
                                        role="cell"
                                        data-label="Prezzo"
                                    >
                                        {formatCurrency(r.unit_price)}
                                    </div>

                                    <div
                                        className="list-cell list-ta-right"
                                        role="cell"
                                        data-label="Totale"
                                    >
                                        {formatCurrency(r.line_total)}
                                    </div>
                                </div>
                            ))}
                    </div>

                    <div
                        className="list-table list-table--summary list-table--compact order-detail-summary"
                        role="table"
                        aria-label="Riepilogo"
                    >
                        <div className="list-row" role="row">
                            <div className="list-cell" role="cell" data-label="Sconti/Promo">
                                Sconti/Promo
                            </div>
                            <div
                                className="list-cell list-ta-right"
                                role="cell"
                                data-label="Valore"
                            >
                                {detailOrder
                                    ? detailOrder.sconto_cents > 0
                                        ? `−${formatCurrency(detailOrder.sconto_cents)}`
                                        : "—"
                                    : detailLoading
                                        ? "…"
                                        : "—"}
                            </div>
                        </div>

                        <div className="list-row" role="row">
                            <div
                                className="list-cell"
                                role="cell"
                                data-label="Spese Trasporto"
                            >
                                Spese Trasporto
                            </div>
                            <div
                                className="list-cell list-ta-right"
                                role="cell"
                                data-label="Valore"
                            >
                                {detailOrder
                                    ? detailOrder.logistics_fee
                                        ? formatCurrency(detailOrder.logistics_fee)
                                        : "—"
                                    : detailLoading
                                        ? "…"
                                        : "—"}
                            </div>
                        </div>

                        <div className="list-row" role="row">
                            <div
                                className="list-cell"
                                role="cell"
                                data-label="Data consegna"
                            >
                                Data consegna
                            </div>
                            <div
                                className="list-cell list-ta-right"
                                role="cell"
                                data-label="Valore"
                            >
                                {detailOrder?.data_consegna
                                    ? formatDate(detailOrder.data_consegna)
                                    : "—"}
                            </div>
                        </div>

                        <div className="list-row" role="row">
                            <div className="list-cell" role="cell" data-label="Totale">
                                Totale
                            </div>
                            <div
                                className="list-cell list-ta-right"
                                role="cell"
                                data-label="Valore"
                            >
                                {detailOrder
                                    ? formatCurrency(detailOrder.total_cents)
                                    : detailLoading
                                        ? "…"
                                        : "—"}
                            </div>
                        </div>
                    </div>

                    {detailError && (
                        <div className="list-empty order-detail-error">
                            {detailError}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}